<script lang="ts" setup>
import { FormKitLazyProvider } from '@formkit/vue'
import type { SectionsNewsletterForm } from '@autobid/strapi-integration/rest/components'
import type { FormKitSchemaFormKit } from '@formkit/core'
import { getError } from '@autobid/strapi-integration/utils/getError'
import CommonDialog from '@autobid/ui/components/common/dialog/Dialog.vue'
import FormWrapper from '@autobid/ui/components/common/FormWrapper.vue'
import { useFormKitSchemaFactory } from '@autobid/ui/composables/useFormKit'
import { CAPTCHA_CHECKBOX_ID } from '@autobid/ui/constants/CAPTCHA_CHECKBOX_ID'
import { FormKit, FormKitSchema } from '@formkit/vue'
import { useMutation } from '@tanstack/vue-query'
import { ref } from 'vue'

const props = defineProps<Props>()
const route = useRoute()
const router = useRouter()

const queryEmail = route.query.email as string
const queryToken = route.query.token as string
const isConfirm = computed(() => route.query.type === 'confirm') // only `type` should be reactive

interface Props {
  data: SectionsNewsletterForm
}

const { $trpc } = useNuxtApp()

const { t } = useI18n()
const model = ref<Record<string, string>>({})
const { init } = useFormKitSchemaFactory()
const formKit = await init({
  schema: props.data.formSchema as FormKitSchemaFormKit[],
  captcha: true,
  onGetDefaults: (data) => {
    model.value = {
      ...model.value,
      ...data,
    }
  },
})

interface DialogMessage {
  title: string
  heading: string
  text: string | null
}

const dialogMessage = ref<DialogMessage | null>(null)
const isDialogOpen = ref(false)

async function sendVerificationEmail() {
  return await $trpc.webapi.registerNewsletter.mutate({
    formData: {
      ...model.value,
      privacy: model.value.privacy ? 1 : 0,
    },
    captcha: model.value[CAPTCHA_CHECKBOX_ID],
    url: `${window.location.href}?${new URLSearchParams({
      type: 'confirm',
      email: model.value.email,
    })}`, // api adds token to url
  })
}

async function confirmEmail() {
  return await $trpc.webapi.confirmNewsletter.mutate({
    email: queryEmail,
    token: queryToken,
  })
}
const { mutate: handleSubmit, isLoading: isSubmitting } = useMutation({
  mutationFn: sendVerificationEmail,
  onError: (unknownError) => {
    const { message, handled } = getError(unknownError)

    if (!handled) {
      dialogMessage.value = {
        title: t('newsletter-registration-modal.title'),
        heading: message,
        text: null,
      }
    }

    isDialogOpen.value = true
  },
  onSuccess: () => {
    dialogMessage.value = {
      title: t('newsletter-registration-modal.title'),
      heading: t('newsletter-registration-modal.heading'),
      text: t('newsletter-registration-modal.text'),
    }
    isDialogOpen.value = true
  },
})
const { mutate: handleConfirmation, isLoading: isConfirming } = useMutation({
  mutationFn: confirmEmail,
  onError: (unknownError) => {
    const { message, handled } = getError(unknownError)

    if (!handled) {
      dialogMessage.value = {
        title: t('newsletter-confirmation-modal.title'),
        heading: message,
        text: null,
      }
    }

    isDialogOpen.value = true
    router.push({ query: {} })
  },
  onSuccess: () => {
    dialogMessage.value = {
      title: t('newsletter-confirmation-modal.title'),
      heading: t('newsletter-confirmation-modal.heading'),
      text: null,
    }
    isDialogOpen.value = true
    router.push({ query: {} })
  },
})

function onClose() {
  const ANIMATION_DURATION = 300
  isDialogOpen.value = false
  setTimeout(() => {
    dialogMessage.value = null
  }, ANIMATION_DURATION)
}

onMounted(() => {
  if (isConfirm.value) {
    handleConfirmation()
  }
})
</script>

<template>
  
<FormKitLazyProvider config-file="true">
<FormWrapper
    v-if="!isConfirm"
    :title="data.title"
    :width="data.width"
    :alignment="data.alignment?.name"
  >
    <FormKit
      v-model="model"
      type="form"
      :disabled="isSubmitting"
      @submit="() => handleSubmit()"
    >
      <FormKitSchema :schema="formKit.schema" />
    </FormKit>
  </FormWrapper>

  <div v-else class="min-h-[20vh]">
    <h2 class="py-4 text-center text-3xl">
      {{ $t('newsletter-confirmation.title') }}
      <br>
      <LazyCommonSpinner v-if="isConfirming" />
    </h2>
  </div>

  <CommonDialog :opened="isDialogOpen" @close="onClose">
    <template #title>
      <span>
        {{ dialogMessage?.title }}
      </span>
    </template>
    <template #content>
      <h2 class="py-4 text-center text-3xl">
        {{ dialogMessage?.heading }}
      </h2>
      <h2 v-if="dialogMessage?.text" class="text-center text-2xl">
        {{ dialogMessage.text }}
      </h2>
    </template>
    <template #buttons>
      <ElementsButton type="block" @click="onClose">
        {{
          $t('close')
        }}
      </ElementsButton>
    </template>
  </CommonDialog>
</FormKitLazyProvider>

</template>

<style lang="scss">
[data-message-type='ui'] {
  margin-top: 1rem;
}
</style>
